import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Table,
    TableBody
} from "@material-ui/core";
import {
    AttachFile as AttachFileIcon,
    FlashOnOutlined as FlashOnOutlinedIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../TableRowSkeleton";
import { getTokens } from "../../styles/tokens";
import { StatusBadge, EmptyState } from "../ui";
import { usePanelStyles, ModalTitle, SearchField } from "../FormFields/panel";

const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
        content: {
            display: "flex",
            flexDirection: "column",
            minHeight: 320,
        },
        searchField: {
            flex: "none",
            maxWidth: "100%",
            marginBottom: 12,
        },
        listContainer: {
            flex: 1,
            minHeight: 0,
            padding: 0,
            overflow: "auto",
            border: `1px solid ${t.line}`,
            borderRadius: t.radius.sm,
            ...theme.scrollbarStyles,
        },
        listItem: {
            cursor: "pointer",
            padding: "10px 14px",
            borderBottom: `1px solid ${t.lineRow}`,
            "&:last-child": { borderBottom: "none" },
            "&:hover": { backgroundColor: t.rowHover },
        },
        head: {
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
        },
        shortcode: {
            fontSize: 14,
            fontWeight: 600,
            color: t.text,
            overflowWrap: "anywhere",
        },
        messagePreview: {
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            marginTop: 3,
            fontSize: 13,
            lineHeight: 1.45,
            color: t.textSecondary,
        },
    };
});

const QuickMessageModal = ({ open, onClose, onSelect, companyId, userId }) => {
    const classes = useStyles();
    const p = usePanelStyles();
    const [quickMessages, setQuickMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [filteredMessages, setFilteredMessages] = useState([]);

    // Buscar respostas rápidas
    useEffect(() => {
        if (open && companyId) {
            fetchQuickMessages();
        }
    }, [open, companyId, userId]);

    // Filtrar mensagens baseado na busca
    useEffect(() => {
        if (searchParam.trim() === "") {
            setFilteredMessages(quickMessages);
        } else {
            const filtered = quickMessages.filter(message =>
                message.shortcode.toLowerCase().includes(searchParam.toLowerCase()) ||
                message.message.toLowerCase().includes(searchParam.toLowerCase())
            );
            setFilteredMessages(filtered);
        }
    }, [searchParam, quickMessages]);

    const fetchQuickMessages = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/quick-messages/list", {
                params: {
                    companyId,
                    userId,
                    isOficial: "false",
                    status: "active"
                }
            });
            setQuickMessages(data || []);
        } catch (err) {
            console.error("Erro ao buscar respostas rápidas:", err);
            toastError(err);
            setQuickMessages([]);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSearchParam("");
        setQuickMessages([]);
        setFilteredMessages([]);
        onClose();
    };

    const handleSelectMessage = (message) => {
        onSelect(message);
        handleClose();
    };

    const handleSearchChange = (event) => {
        setSearchParam(event.target.value);
    };

    const renderMessageItem = (message) => {
        const hasMedia = message.mediaPath || message.mediaName;

        return (
            <ListItem
                key={message.id}
                button
                className={classes.listItem}
                onClick={() => handleSelectMessage(message)}
            >
                <ListItemText
                    disableTypography
                    primary={
                        <div className={classes.head}>
                            <span className={classes.shortcode}>
                                #{message.shortcode}
                            </span>
                            {hasMedia && (
                                <StatusBadge
                                    color="neutral"
                                    size="sm"
                                    icon={<AttachFileIcon />}
                                    label={i18n.t("quickMessages.hasMedia")}
                                />
                            )}
                            {message.geral && (
                                <StatusBadge
                                    color="brand"
                                    size="sm"
                                    dot={false}
                                    label={i18n.t("quickMessages.global")}
                                />
                            )}
                        </div>
                    }
                    secondary={
                        message.message && (
                            <div className={classes.messagePreview}>
                                {message.message}
                            </div>
                        )
                    }
                />
            </ListItem>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Table size="small">
                    <TableBody>
                        <TableRowSkeleton columns={1} />
                    </TableBody>
                </Table>
            );
        }

        if (filteredMessages.length === 0) {
            return (
                <EmptyState
                    icon={<FlashOnOutlinedIcon />}
                    title={searchParam
                        ? i18n.t("quickMessages.noResultsFound")
                        : i18n.t("quickMessages.noQuickMessages")
                    }
                />
            );
        }

        return (
            <List className={classes.listContainer}>
                {filteredMessages.map((message) => renderMessageItem(message))}
            </List>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            scroll="paper"
            classes={{ paper: p.dialogPaper }}
        >
            <ModalTitle
                title={i18n.t("quickMessages.selectMessage")}
                onClose={handleClose}
            />

            <DialogContent className={`${p.modalBody} ${classes.content}`}>
                <SearchField
                    className={classes.searchField}
                    placeholder={i18n.t("quickMessages.searchPlaceholder")}
                    value={searchParam}
                    onChange={handleSearchChange}
                />

                {renderContent()}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>
                    {i18n.t("quickMessages.buttons.cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuickMessageModal;